import { useTranslation } from "react-i18next";
import { FormCard } from "../FormCard";
import { normalizeDayOfWeekValue } from "./tariffOptions";
import {
    formatPriceInclVat,
    formatSpotCalculationExcl,
    formatSpotCalculationIncl,
} from "./wizard/EnergyPriceStep";

const PRICE_GRID =
    "grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-6 w-full";

const TariffElementsOverview = ({ elements = [], currency = "", spotEnergy = null }) => {
    const { t } = useTranslation();
    const { energyRows, extraRows } = buildTariffPriceRows(elements, currency, spotEnergy, t);

    return (
        <FormCard title={t("tariff.preview.pricing")} headerClassName="mb-5">
            {energyRows.length === 0 && extraRows.length === 0 ? (
                <p className="text-sm text-mute-500">{t("tariff.preview.noPrices")}</p>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-16 gap-y-10 lg:items-start">
                    {energyRows.length > 0 && (
                        <PriceSection title={t("common.energy")} rows={energyRows} t={t} />
                    )}
                    {extraRows.length > 0 && (
                        <PriceSection title={t("tariff.steps.extraFees")} rows={extraRows} t={t} />
                    )}
                </div>
            )}
        </FormCard>
    );
};

export function buildTariffPriceRows(elements = [], currency = "", spotEnergy = null, t) {
    const visible = (elements || []).filter((element) => !element.isDeleted);
    const energyRows = spotEnergy
        ? toSpotEnergyRows(spotEnergy, currency, t)
        : toPriceRows(visible, currency, "energy", t);
    const extraRows = toPriceRows(visible, currency, "extra", t);
    return { energyRows, extraRows };
}

function PriceSection({ title, rows, t }) {
    return (
        <div className="min-w-0">
            <h3 className="text-lg font-semibold mb-3">{title}</h3>
            <div className={PRICE_GRID}>
                <div className="pb-3 border-b border-mute-200" />
                <div className="pb-3 border-b border-mute-200 text-sm text-mute-600 text-right whitespace-nowrap">
                    {t("tariff.preview.exclVat")}
                </div>
                <div className="pb-3 border-b border-mute-200 text-sm text-mute-600 text-right whitespace-nowrap">
                    {t("tariff.preview.inclVat")}
                </div>
                {rows.map((row, index) => (
                    <PriceGroup key={row.key} row={row} first={index === 0} />
                ))}
            </div>
        </div>
    );
}

function PriceGroup({ row, first }) {
    const blocks = row.blocks || [row];
    return (
        <>
            <div
                className={`col-span-3 ${
                    first ? "pt-4 pb-2" : "mt-4 pt-5 pb-2 border-t border-mute-200"
                }`}
            >
                <div className="text-sm font-semibold">{row.label}</div>
                {row.description ? (
                    <p className="text-sm text-mute-500 mt-1">{row.description}</p>
                ) : null}
            </div>
            {blocks.map((block, index) => (
                <PriceLine key={`${row.key}-${index}`} block={block} />
            ))}
        </>
    );
}

function PriceLine({ block }) {
    return (
        <>
            <div className="py-1.5 text-sm text-mute-700">
                {block.detail}
                {block.meta ? <span className="text-mute-500"> · {block.meta}</span> : null}
            </div>
            <div className="py-1.5 text-sm font-semibold text-right tabular-nums whitespace-nowrap">
                {block.value || "—"}
            </div>
            <div className="py-1.5 text-sm text-mute-700 text-right tabular-nums whitespace-nowrap">
                {block.inclValue || "—"}
            </div>
        </>
    );
}

function toSpotEnergyRows(energy, currency, t) {
    const blocks = [
        {
            detail: t("tariff.preview.spotPlusSurcharge"),
            value: formatSpotCalculationExcl(energy.price, currency, t),
            inclValue: formatSpotCalculationIncl(energy.price, energy.vat, currency, t),
        },
    ];
    if (hasSpotBound(energy.energyPriceMinExclVat)) {
        blocks.push({
            detail: t("tariff.preview.minKwh"),
            value: formatSpotBound(energy.energyPriceMinExclVat, 0, currency),
            inclValue: formatSpotBound(energy.energyPriceMinExclVat, energy.vat, currency),
        });
    }
    if (hasSpotBound(energy.energyPriceMaxExclVat)) {
        blocks.push({
            detail: t("tariff.preview.maxKwh"),
            value: formatSpotBound(energy.energyPriceMaxExclVat, 0, currency),
            inclValue: formatSpotBound(energy.energyPriceMaxExclVat, energy.vat, currency),
        });
    }
    return [groupedRow("spot-energy", t("tariff.modeSpotTitle"), "", blocks)];
}

function hasSpotBound(value) {
    return value !== "" && value != null;
}

function formatSpotBound(price, vat, currency) {
    const formatted = formatPriceInclVat(price, vat, currency);
    if (!formatted) {
        return "";
    }
    return `${formatted}/kWh`;
}

function toPriceRows(elements, currency, kind, t) {
    return (elements || []).flatMap((element, index) => {
        const components = getComponents(element).filter((pc) => componentKind(element, pc) === kind);
        if (!components.length) {
            return [];
        }
        const prefix = element.tariffElementPublicId || `element-${index}`;
        if (kind === "energy") {
            return components.map((pc, pcIndex) =>
                groupedRow(`${prefix}-${pc.type}-${pcIndex}`, energyLabel(element, t), "", [
                    priceBlock(element, pc, currency, t),
                ])
            );
        }
        if (isLaneElement(element)) {
            return [mergedLaneRow(element, components, currency, prefix, t)];
        }
        return components.map((pc, pcIndex) => {
            const feeKey = extraFeeKey(element, pc);
            const label = componentLabel(element, pc, t);
            return groupedRow(`${prefix}-${pc.type}-${pcIndex}`, label, feeDescription(feeKey, t), [
                priceBlock(element, pc, currency, t),
            ]);
        });
    });
}

function mergedLaneRow(element, components, currency, prefix, t) {
    const ordered = [
        ...components.filter((pc) => pc.type === "FLAT"),
        ...components.filter((pc) => pc.type === "TIME"),
        ...components.filter((pc) => pc.type !== "FLAT" && pc.type !== "TIME"),
    ];
    const feeKey = extraFeeKey(element, ordered[0]);
    const label = componentLabel(element, ordered[0], t);
    return groupedRow(
        prefix,
        label,
        feeDescription(feeKey, t),
        ordered.map((pc) => priceBlock(element, pc, currency, t))
    );
}

function groupedRow(key, label, description, blocks) {
    return { key, label, description, blocks };
}

function priceBlock(element, pc, currency, t) {
    return {
        detail: lineLabel(pc, t),
        value: formatComponentPrice(pc, currency, false),
        inclValue: formatComponentPrice(pc, currency, true),
        meta: rowMeta(pc, t),
    };
}

function lineLabel(pc, t) {
    if (pc?.type === "ENERGY") {
        return t("tariff.preview.perKwh");
    }
    if (pc?.type === "FLAT") {
        return t("common.fixedFee");
    }
    if (pc?.type === "TIME" || pc?.type === "PARKING_TIME") {
        return t("tariff.preview.hourlyRate");
    }
    return pc?.type || "";
}

function extraFeeKey(element, pc) {
    const restrictions = element?.restrictions || {};
    const reservation = String(restrictions.reservation || "").toUpperCase();
    const booking = String(restrictions.booking || "").toUpperCase();

    if (reservation === "RESERVATION") {
        return "bookingHold";
    }
    if (reservation === "RESERVATION_EXPIRES" || booking === "RESERVATION_EXPIRES") {
        return "bookingNoShow";
    }
    if (booking === "BOOKING_EXPIRES") {
        return "bookingNoShow";
    }
    if (booking === "BOOKING_OVERTIME") {
        return "bookingOvertime";
    }
    if (booking === "BOOKING_CANCELLATION_FEES") {
        return "bookingCancellation";
    }
    if (booking === "BOOKING") {
        return "bookingWindow";
    }
    if (booking) {
        return "bookingOther";
    }
    if (pc.type === "ENERGY") {
        return "energy";
    }
    if (pc.type === "TIME") {
        return "chargingTime";
    }
    if (pc.type === "PARKING_TIME") {
        return "parkingTime";
    }
    if (pc.type === "FLAT") {
        return "startFee";
    }
    return "";
}

function feeDescription(feeKey, t) {
    if (!feeKey || feeKey === "energy" || feeKey === "bookingOther") {
        return "";
    }
    return t(`tariff.${feeKey}Description`, { defaultValue: "" });
}

function getComponents(element) {
    return element?.priceComponents || element?.price_components || [];
}

function isLaneElement(element) {
    const restrictions = element?.restrictions || {};
    return Boolean(restrictions.reservation || restrictions.booking);
}

function componentKind(element, pc) {
    if (isLaneElement(element)) {
        return "extra";
    }
    return pc.type === "ENERGY" ? "energy" : "extra";
}

function componentLabel(element, pc, t) {
    const feeKey = extraFeeKey(element, pc);
    if (feeKey === "bookingOther") {
        const booking = String(element?.restrictions?.booking || "").toUpperCase();
        return t("tariff.bookingOther", {
            booking: t(`tariff.booking.${booking}`, { defaultValue: booking.replaceAll("_", " ") }),
        });
    }
    if (feeKey === "energy") {
        return t("common.energy");
    }
    if (!feeKey) {
        return pc.type;
    }
    return t(`tariff.${feeKey}`);
}

function energyLabel(element, t) {
    const parts = restrictionParts(element, t);
    return parts.length ? parts.join(" · ") : "24/7";
}

function restrictionParts(element, t) {
    const restrictions = element?.restrictions || {};
    const parts = [];
    const days = formatDayRange(restrictions.dayOfWeek || [], t);
    if (days) {
        parts.push(days);
    }
    if (restrictions.startTime || restrictions.endTime) {
        parts.push(
            `${formatTime(restrictions.startTime) || "—"}–${formatTime(restrictions.endTime) || "—"}`
        );
    }
    if (restrictions.minPower != null || restrictions.maxPower != null) {
        parts.push(`${restrictions.minPower ?? 0}–${restrictions.maxPower ?? "+"} kW`);
    }
    return parts;
}

const DAY_ORDER = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

function formatDayRange(days, t) {
    const values = DAY_ORDER.filter((day) =>
        (days || []).some((value) => normalizeDayOfWeekValue(value) === day)
    );
    if (!values.length) {
        return "";
    }
    if (values.length === 7) {
        return t("tariff.preview.everyDay");
    }
    if (values.length === 5 && values[0] === "MONDAY" && values[4] === "FRIDAY") {
        return `${t("weekdayShort.MONDAY")}–${t("weekdayShort.FRIDAY")}`;
    }
    if (values.length === 2 && values[0] === "SATURDAY" && values[1] === "SUNDAY") {
        return t("tariff.preview.weekend");
    }
    const indexes = values.map((day) => DAY_ORDER.indexOf(day));
    const consecutive = indexes.every((index, i) => i === 0 || index === indexes[i - 1] + 1);
    if (consecutive && values.length > 1) {
        return `${t(`weekdayShort.${values[0]}`)}–${t(`weekdayShort.${values[values.length - 1]}`)}`;
    }
    return values.map((day) => t(`weekdayShort.${day}`)).join(", ");
}

function formatTime(value) {
    if (!value) {
        return "";
    }
    const text = String(value);
    return text.length >= 5 ? text.slice(0, 5) : text;
}

function formatComponentPrice(pc, currency, inclVat) {
    const amount = inclVat ? priceInclVat(pc) : toPriceNumber(pc?.price);
    if (amount == null) {
        return inclVat ? "" : "-";
    }
    const unit =
        pc.type === "ENERGY" ? "/kWh" : pc.type === "TIME" || pc.type === "PARKING_TIME" ? "/h" : "";
    const formatted = formatPriceAmount(amount);
    return currency ? `${formatted} ${currency}${unit}` : `${formatted}${unit}`;
}

function toPriceNumber(value) {
    if (value === "" || value == null) {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function priceInclVat(pc) {
    const excl = toPriceNumber(pc?.price);
    if (excl == null) {
        return null;
    }
    const vatPct = pc?.vat === "" || pc?.vat == null ? 0 : Number(pc.vat);
    if (!Number.isFinite(vatPct)) {
        return null;
    }
    return excl * (1 + vatPct / 100);
}

function formatPriceAmount(value) {
    const rounded = Math.round(value * 10000) / 10000;
    if (Number.isInteger(rounded)) {
        return String(rounded);
    }
    return String(rounded);
}

function rowMeta(pc, t) {
    return formatPerStepCharge(pc, t);
}

function formatPerStepCharge(pc, t) {
    if (pc?.type !== "TIME" && pc?.type !== "PARKING_TIME") {
        return "";
    }
    const step = Number(pc.stepSize);
    if (!Number.isFinite(step) || step <= 1) {
        return "";
    }
    return t("tariff.everyStarted", { unit: formatStepLabel(step, t) });
}

function formatStepLabel(step, t) {
    if (step % 60 === 0) {
        return t("tariff.stepMin", { n: step / 60 });
    }
    return t("tariff.stepSec", { n: step });
}

export default TariffElementsOverview;
